// Rotates announcement messages one at a time; pauses on hover, focus, hidden tab, reduced motion, or the explicit pause button.
class SAnnouncementRotator extends HTMLElement {
  #abort = null;
  #timer = 0;
  #index = 0;
  #paused = { hover: false, focus: false, hidden: false, motion: false, user: false };

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.region = this.querySelector('[data-slides]');
    this.toggle = this.querySelector('[command="--toggle"]');
    if (this.slides.length < 2) {
      this.querySelectorAll('[command^="--"]').forEach((button) => { button.hidden = true; });
      return;
    }

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused.motion = motion.matches;
    motion.addEventListener('change', () => { this.#paused.motion = motion.matches; this.#sync(); }, { signal });
    this.addEventListener('pointerenter', () => { this.#paused.hover = true; this.#sync(); }, { signal });
    this.addEventListener('pointerleave', () => { this.#paused.hover = false; this.#sync(); }, { signal });
    this.addEventListener('focusin', () => { this.#paused.focus = true; this.#sync(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.#paused.focus = false; this.#sync(); }
    }, { signal });
    document.addEventListener('visibilitychange', () => { this.#paused.hidden = document.hidden; this.#sync(); }, { signal });
    this.addEventListener('command', (event) => {
      if (event.command === '--prev' || event.command === '--next') {
        this.#show(this.#index + (event.command === '--next' ? 1 : -1));
        // Restart the timer so a manual step gets a full interval before auto-advancing.
        this.#sync();
        return;
      }
      if (event.command !== '--toggle') return;
      this.#paused.user = !this.#paused.user;
      this.#sync();
    }, { signal });

    this.#show(0);
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearInterval(this.#timer);
  }

  #sync() {
    const paused = Object.values(this.#paused).some(Boolean);
    clearInterval(this.#timer);
    if (!paused) {
      const interval = Number(this.dataset.interval) || 4000;
      this.#timer = setInterval(() => this.#show(this.#index + 1), interval);
    }
    if (this.region) this.region.setAttribute('aria-live', paused ? 'polite' : 'off');
    if (this.toggle) {
      this.toggle.setAttribute('aria-pressed', String(this.#paused.user));
      this.toggle.setAttribute('aria-label', this.#paused.user ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.toggle.dataset.state = this.#paused.user ? 'paused' : 'playing';
    }
  }

  #show(next) {
    this.#index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    });
  }
}

if (!customElements.get('s-announcement-rotator')) customElements.define('s-announcement-rotator', SAnnouncementRotator);
